import React, { useMemo } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { Badge } from "artisn-ui-react";

import useSignOut from "hooks/useSignOut";
import Styles from "./ProfileNavigation.styles";
import { tabs } from "./ProfileNavigation.helpers";
import useAuth from "contexts/auth/auth.context.hooks";
import { ProfileNavigationProps as Props } from "./ProfileNavigation.types";
import Button from "components/global/Button/Button";
import { useFetchOrderHistory } from "services/orders/orders.service.hooks";
import { useFetchInvoiceSettings } from "services/billingData/billingData.service.hooks";
import CONSTANTS from "config/constants";
import useTalkShop from "contexts/talkShop/talkShop/talkShop.context.hooks";

import UserSVG from "../../../../public/assets/images/TabsMenu/user.svg";
import ArrowRightSVG from "../../../../public/assets/images/arrow-right-black.svg";
import LogoutSVG from "../../../../public/assets/images/logout.svg";

const { WITH_TALK_SHOP, WITH_ANONYMOUS } = CONSTANTS.FEATURE_FLAGS;

const ProfileNavigation: React.FC<Props> = props => {
  const { className } = props;
  const { pathname: currentPage, push } = useRouter();
  const { isAnonymous } = useAuth();
  const signOut = useSignOut();
  const orders = useFetchOrderHistory("IN_PROGRESS");
  const { data: ordersInProgress } = orders;
  const numberOfOrders = ordersInProgress?.length ?? 0;
  const { data: invoiceSettings } = useFetchInvoiceSettings();
  const { allowInvoicing } = invoiceSettings ?? {};
  const { talkShopProvider } = useTalkShop();

  const tabsToRender = useMemo(
    () =>
      tabs.filter(tab => {
        if (!tab) return false;
        const { tabName, isPublic } = tab;
        const isBillingDataTab = tabName === "Datos de facturación";
        const showBillingData = isBillingDataTab && allowInvoicing;

        if (isBillingDataTab) {
          if (allowInvoicing) {
            return true;
          }
          return false;
        }

        if ((!isPublic && !isAnonymous) || isPublic || showBillingData) {
          return true;
        }

        return false;
      }),
    [allowInvoicing, isAnonymous]
  );

  const goToSignInHandler = () => {
    push({
      pathname: "/signin",
      query: {
        redirect: "profile"
      }
    });
  };

  if (talkShopProvider && WITH_TALK_SHOP) return null;

  return (
    <Styles className={`ProfileNavigation ${className}`}>
      {isAnonymous ? (
        <Button
          className="ProfileNavigation__login-button"
          onClick={goToSignInHandler}
        >
          <div className="ProfileNavigation__login-button__left-content">
            <UserSVG
              className="ProfileNavigation__login-button__user-icon"
              viewBox="0 0 28 28"
            />
            <span className="ProfileNavigation__login-button__text">
              Ingresa con tu cuenta
            </span>
          </div>
          <ArrowRightSVG className="ProfileNavigation__login-button__right-content" />
        </Button>
      ) : null}
      {tabsToRender.map((tab, index) => {
        if (!tab) return null;
        const { icon, tabName, isExternal, url = "/", isPublic } = tab;

        if (
          tabName === "Historial de compra" &&
          isAnonymous &&
          !WITH_ANONYMOUS
        ) {
          return null;
        }

        if (isPublic) {
          if (isExternal) {
            return (
              <a
                key={index}
                className={`ProfileNavigation__tab ${
                  currentPage.includes(url) ? "is-currently-active" : ""
                }`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {icon}
                <p className="ProfileNavigation__tab-name">{tabName}</p>
              </a>
            );
          }

          return (
            <Link key={index} href={url} passHref>
              <a
                className={`ProfileNavigation__tab ${
                  currentPage.includes(url) ? "is-currently-active" : ""
                }`}
              >
                {icon}
                <p className="ProfileNavigation__tab-name">{tabName}</p>
                {tabName === "Mis ordenes" && numberOfOrders > 0 ? (
                  <Badge
                    className="ProfileNavigation__badge"
                    value={`${numberOfOrders}`}
                  >
                    {}
                  </Badge>
                ) : null}
              </a>
            </Link>
          );
        }

        if (isAnonymous) return null;

        return (
          <Link key={index} href={url} passHref>
            <a
              className={`ProfileNavigation__tab ${
                currentPage.includes(url) ? "is-currently-active" : ""
              }`}
            >
              {icon}
              <p className="ProfileNavigation__tab-name">{tabName}</p>
            </a>
          </Link>
        );
      })}
      {!isAnonymous ? (
        <button className="ProfileNavigation__logout-button" onClick={signOut}>
          <LogoutSVG className="ProfileNavigation__logout-button__icon" /> Salir
        </button>
      ) : null}
    </Styles>
  );
};

ProfileNavigation.defaultProps = {
  className: ""
};

export default ProfileNavigation;
